/**
 * Rendering for table cells, driven by the property's HubSpot type.
 *
 * A last invoice amount arriving as 9772.0 and printed as "9772.0" is a number
 * on a page, not a figure. Currency, count and date each get the treatment
 * they need, and figures are set in a pill so they read as values at a glance
 * rather than as more text in a row.
 */

const CURRENCY = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NUMBER = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });

// HubSpot property names that hold money but are typed as plain numbers.
const MONEY_HINTS = /amount|revenue|price|cost|value|invoice|arr|mrr|target|discount|fee/i;

export function isMoney(column) {
  if (!column) return false;
  if (column.type === "currency") return true;
  return column.type === "number" && MONEY_HINTS.test(column.name || "");
}

export function isNumeric(column) {
  return column?.type === "number" || column?.type === "currency";
}

export function formatMoney(value) {
  const number = Number(value);
  return Number.isFinite(number) ? CURRENCY.format(number) : String(value ?? "");
}

export function formatNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? NUMBER.format(number) : String(value ?? "");
}

export function formatDate(value) {
  if (value === null || value === undefined || value === "") return "";
  // HubSpot dates arrive as ISO strings or epoch milliseconds.
  const raw = String(value);
  const date = /^-?\d+$/.test(raw) ? new Date(Number(raw)) : new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** A figure set in a pill, so it reads as a value rather than as more text. */
export function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-[#efefef] text-[#323c44]",
    // Rare's mint tint for money, so a figure reads as a value at a glance.
    money: "bg-[#e8faf5] text-[#1f5d74]",
    good: "bg-[#eef8e2] text-[#3f7a06]",
    warn: "bg-[#f7efe6] text-[#8a6440]",
    bad: "bg-[#fbe9eb] text-[#c2192b]",
  };
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[13px] font-semibold tabular-nums ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

/** Render one cell according to its column's type. */
export function renderCell(value, column) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-[#a9b3bb]">—</span>;
  }
  if (isMoney(column)) return <Pill tone="money">{formatMoney(value)}</Pill>;
  if (isNumeric(column)) return <Pill>{formatNumber(value)}</Pill>;
  if (column?.type === "date" || column?.type === "datetime") {
    return <span className="tabular-nums text-[#323c44]">{formatDate(value)}</span>;
  }
  return <span className="text-[#323c44]">{String(value)}</span>;
}

/** Right-align the columns that hold figures. */
export function alignFor(column) {
  return isNumeric(column) ? "text-right" : "text-left";
}
